import { useState, useMemo, useRef, useEffect, type ReactNode, useCallback } from "react";
import type { Task } from "~/types/api";
import { TabWindowsActionsContext, TabWindowsStateContext } from "./tab-windows-context";
import type { TabWindow } from "../../task.types";
import { useDetailView } from "../hooks/use-detail-view";
import { useItemSelection } from "../hooks/use-item-selection";
import { useWindowActions } from "../hooks/use-window-actions";
import { useWindowState } from "../hooks/use-window-state";
import { useWindowInitializer } from "../hooks/window-initialize/use-window-initializer";

type Props = {
  children: ReactNode;
  tasks?: Task[];
};

export const TabWindowsProvider = <T,>({ children, tasks }: Props) => {
  const [windows, setWindows] = useState<TabWindow<T>[]>([]);
  const windowsRef = useRef(windows);

  useEffect(() => {
    windowsRef.current = windows;
  }, [windows]);

  const { isInitializing } = useWindowInitializer<T>({ tasks, setWindows });

  const windowActions = useWindowActions<T>({ setWindows });

  const detailView = useDetailView<T>({ setWindows, windowsRef });

  const { handleItemSelection } = useItemSelection<T>({
    setWindows,
    windowsRef,
    activePanelWindowId: detailView.activePanelWindowId,
    setActivePanelWindowId: detailView.setActivePanelWindowId,
    setDetailViewItem: detailView.setDetailViewItem,
    updateDetailViewForWindow: detailView.updateDetailViewForWindow,
  });

  const computedState = useWindowState<T>({ windows });

  const getWindow = useCallback(
    (windowId: string) => windowsRef.current.find((w) => w.id === windowId),
    []
  );

  const stateValue = useMemo(
    () => ({
      windows,
      ...computedState,
      ...detailView,
      isInitializing,
    }),
    [windows, computedState, detailView, isInitializing]
  );

  const closeWindow = useCallback(
    (windowId: string) => {
      windowActions.closeWindow(windowId);
      if (detailView.activePanelWindowId === windowId) {
        detailView.setActivePanelWindowId(null);
        detailView.setDetailViewItem(null);
      }
    },
    [windowActions, detailView]
  );

  const actionsValue = useMemo(
    () => ({
      ...windowActions,
      closeWindow,
      handleItemSelection,
      ...detailView,
      getWindow,
    }),
    [windowActions, closeWindow, handleItemSelection, detailView, getWindow]
  );

  return (
    <TabWindowsStateContext.Provider value={stateValue}>
      <TabWindowsActionsContext.Provider value={actionsValue}>
        {children}
      </TabWindowsActionsContext.Provider>
    </TabWindowsStateContext.Provider>
  );
};
